import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'

import * as Yup from 'yup'

import { industryActions } from 'store/Industry'
import { all } from 'store/Industry/Industry.selector'

import { submitOnEnter } from 'utils/submitOnEnter'

import { IndustryCertificateTable } from './IndustryCertificateTable'

import { Input } from 'components/Input'
import { Button } from 'components/Button'
import { Pagination } from 'components/Pagination'
import { InputCpfCnpj } from 'components/InputCpfCnpj'

import * as S from './styled'

const INITIAL_STATE = {
  cnpj: '',
  inspectionNum: '',
  page: 0,
  limit: 10
}

const schema = Yup.object().shape({
  cnpj: Yup.string(),
  inspectionNum: Yup.string()
})

export function IndustryCertificateList() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { industriesWithCertificates, loading } = useSelector(all)

  const [filter, setFilter] = useState({ ...INITIAL_STATE })
  const [errors, setErrors] = useState({ ...INITIAL_STATE })

  function handleInput(value: string | Date, name: string) {
    setErrors({ ...errors, [name]: '' })
    setFilter({ ...filter, [name]: value })
  }

  function onFilterPaginate(params = {}) {
    dispatch(industryActions.fetchAllWithCertificates({ params }))
  }

  function clearFilter() {
    const params = {
      ...INITIAL_STATE,
      limit: 10,
      page: 0
    }
    setFilter(params)
    setErrors({ ...INITIAL_STATE })
    dispatch(industryActions.fetchAllWithCertificates({ params }))
  }

  async function onFilter() {
    try {
      const params = await schema.validate(filter, {
        abortEarly: false,
        stripUnknown: true
      })

      const paramsFormatted = {
        ...params,
        cnpj: params.cnpj.replace(/\D/g, '')
      }

      dispatch(
        industryActions.fetchAllWithCertificates({ params: paramsFormatted })
      )
    } catch (ex) {
      const pairs = ex.inner.map(({ path, message }) => [
        path,
        message.replace(`${path} `, '')
      ])
      setErrors(Object.fromEntries(pairs) as typeof INITIAL_STATE)
    }
  }

  useEffect(() => {
    dispatch(industryActions.fetchAllWithCertificates())
  }, [dispatch])

  return (
    <S.Wrapper container>
      <S.FullGrid item xs={12}>
        <S.GridHeader>
          <S.GridTitle item container xs={12}>
            <S.BoxTitle>{t('common:filterCertifications')}</S.BoxTitle>
            <S.BoxLine />
          </S.GridTitle>
        </S.GridHeader>

        <S.GridFilter container>
          <S.GridInput item xs={12} sm={6} md={4}>
            <InputCpfCnpj
              error={Boolean(errors.cnpj)}
              fullWidth
              helperText={errors.cnpj}
              label={t('common:CNPJ')}
              onInput={value => handleInput(value, 'cnpj')}
              onKeyPress={submitOnEnter(onFilter)}
              type="cnpj"
              value={filter.cnpj}
            />
          </S.GridInput>

          <S.GridInput item xs={12} sm={6} md={4}>
            <Input
              error={Boolean(errors.inspectionNum)}
              fullWidth
              helperText={errors.inspectionNum}
              label={t('common:SIF')}
              onInput={value => handleInput(value, 'inspectionNum')}
              onKeyPress={submitOnEnter(onFilter)}
              value={filter.inspectionNum}
            />
          </S.GridInput>

          <S.GridButtons item xs={12} sm={12} md={4}>
            <Button variant="default" onClick={clearFilter} size="medium">
              <p>{t('common:clean')}</p>
            </Button>
            <Button variant="primary" onClick={onFilter} size="medium">
              <p>{t('common:search')}</p>
            </Button>
          </S.GridButtons>
        </S.GridFilter>

        <S.GridTable>
          <IndustryCertificateTable
            rows={industriesWithCertificates.items}
            isLoading={loading}
          />
          {industriesWithCertificates.pagination?.totalItems > 0 && (
            <Pagination
              filters={{
                ...filter,
                limit: industriesWithCertificates.pagination.limit,
                page: industriesWithCertificates.pagination.page
              }}
              limit={industriesWithCertificates.pagination.limit}
              page={industriesWithCertificates.pagination.page}
              totalItems={industriesWithCertificates.pagination.totalItems}
              setFilter={onFilterPaginate}
            />
          )}
        </S.GridTable>
      </S.FullGrid>
    </S.Wrapper>
  )
}
